/**
 * Inequality presets for the inequality-line experiment.
 * These are the canned inequality problems students can work through.
 */

import type { Comparator } from '../algebra/inequality';

export interface InequalityPreset {
  id: string;
  lhs: string;
  comparator: Comparator;
  rhs: string;
}

export const INEQUALITY_PRESETS: readonly InequalityPreset[] = [
  // Linear inequalities
  { id: 'linear-1', lhs: '2x - 5', comparator: '<', rhs: '7' },
  { id: 'linear-2', lhs: 'x + 3', comparator: '<=', rhs: '10' },
  {
    id: 'linear-distribute',
    lhs: '3(x - 2)',
    comparator: '>=',
    rhs: '9',
  },

  // Absolute value inequalities
  {
    id: 'abs-1',
    lhs: 'abs(x - 3)',
    comparator: '<',
    rhs: '2',
  },
  {
    id: 'abs-2',
    lhs: 'abs(x)',
    comparator: '<=',
    rhs: '5',
  },

  // Simple quadratic inequalities
  {
    id: 'quadratic-1',
    lhs: 'x^2 - 4',
    comparator: '>=',
    rhs: '0',
  },
  {
    id: 'quadratic-2',
    lhs: 'x^2 - 5*x + 6',
    comparator: '>',
    rhs: '0',
  },

  // Rational inequalities
  {
    id: 'rational-1',
    lhs: '(x - 1)/(x + 2)',
    comparator: '>=',
    rhs: '0',
  },
  {
    id: 'rational-2',
    lhs: '1/x',
    comparator: '>',
    rhs: '2',
  },

  // Quadratic with discriminant < 0
  {
    id: 'quad-neg-disc',
    lhs: 'x^2 + 1',
    comparator: '>',
    rhs: '0',
  },
  {
    id: 'quad-neg-disc-no-solution',
    lhs: 'x^2 + 1',
    comparator: '<',
    rhs: '0',
  },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: InequalityPreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // lhs and rhs must be non-empty strings
  if (typeof p.lhs !== 'string' || p.lhs.length === 0) {
    return false;
  }
  if (typeof p.rhs !== 'string' || p.rhs.length === 0) {
    return false;
  }

  // comparator must be one of the valid types
  if (!['<', '<=', '>', '>='].includes(p.comparator)) {
    return false;
  }

  return true;
}
